/**
 * ReportRenderer — paints the V2 command-engine response.
 *
 * Walks the ``sections`` array Claude returned and renders each one
 * as a bento card:
 *
 *   • stats  → row of stat cards with the headline number
 *   • bar    → horizontal bar chart (no recharts dep — pure SVG)
 *   • pie    → donut chart
 *   • line   → smoothed sparkline with axis labels
 *   • table  → clean tabular data with alt-row striping
 *   • text   → narrative paragraph
 *
 * Below the sections we render an "action row" (Download / Schedule
 * / Email) and a "suggestions row" (clickable follow-up chips).
 * Click handlers for actions are passed in by the parent — the
 * renderer doesn't know HOW to download a PDF, only what label to
 * show.
 *
 * No external chart library — every viz is hand-rolled SVG so the
 * dashboard's bundle size stays where it is and the colours pick
 * up the brand palette via CSS variables.
 */
import { Card } from "@/components/ui/Card";
import {
  BookOpen,
  Briefcase,
  Calendar,
  CheckCircle2,
  Download,
  ExternalLink,
  FileText,
  Layers,
  Mail,
  Megaphone,
  Receipt,
  Rocket,
  Sparkles,
  TrendingUp,
  User as UserIcon,
  X,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useState, type ReactNode } from "react";
import type {
  Citation,
  OnboardConfirmationSection,
  ReportSection,
} from "@/api/aiCommand";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/hooks/useToast";
import { executeOnboarding } from "@/api/onboarding";

// Categorical palette for multi-bar / multi-segment charts. Each
// bar / slice rotates through these — picked for high contrast at
// adjacent indices and for matching the brand-accent on idx=0.
const PALETTE = [
  "#4F46E5", // indigo
  "#10B981", // emerald
  "#F97316", // orange
  "#A78BFA", // violet
  "#EC4899", // pink
  "#06B6D4", // cyan
  "#F59E0B", // amber
  "#EF4444", // red
  "#14B8A6", // teal
  "#8B5CF6", // purple
  "#64748B", // slate
];

interface Props {
  sections: ReportSection[];
  actions?: string[];
  suggestions?: string[];
  citations?: Citation[];
  onAction?: (label: string) => void;
  onSuggestion?: (text: string) => void;
}

export function ReportRenderer({
  sections,
  actions,
  suggestions,
  citations,
  onAction,
  onSuggestion,
}: Props) {
  if (!sections || sections.length === 0) return null;

  // Index by ref so the tokenizer can do an O(1) lookup per [N]
  // marker. Empty when no citations — the tokenizer then renders
  // the [N] tokens as plain text.
  const citationMap = buildCitationMap(citations);

  return (
    <div className="space-y-3">
      {sections.map((s, i) => (
        <SectionCard key={i} section={s} citations={citationMap} />
      ))}
      {citations && citations.length > 0 && (
        <CitationsFooter citations={citations} />
      )}
      {actions && actions.length > 0 && (
        <ActionRow actions={actions} onAction={onAction} />
      )}
      {suggestions && suggestions.length > 0 && (
        <SuggestionRow suggestions={suggestions} onSuggestion={onSuggestion} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Section dispatcher
// ════════════════════════════════════════════════════════════════════

function SectionCard({
  section,
  citations,
}: {
  section: ReportSection;
  citations: Map<number, Citation>;
}) {
  switch (section.type) {
    case "stats":
      return <StatsCard {...section} />;
    case "bar":
      return <BarCard {...section} />;
    case "pie":
      return <PieCard {...section} />;
    case "line":
      return <LineCard {...section} />;
    case "table":
      return <TableCard {...section} />;
    case "text":
      return <TextCard {...section} citations={citations} />;
    case "onboard_confirmation":
      return <OnboardConfirmationCard {...section} />;
    default:
      return null;
  }
}

function OnboardConfirmationCard(section: OnboardConfirmationSection) {
  const { addToast } = useToast();
  const emp = section.employee;
  const [email, setEmail] = useState(emp.email);
  const [managerId, setManagerId] = useState(emp.manager_id ?? "");
  const [startDate, setStartDate] = useState(emp.start_date);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);

  const fullName = `${emp.first_name} ${emp.last_name}`.trim();
  const managerOptions = [
    { value: "", label: "— No manager —" },
    ...section.manager_candidates.map((m) => ({
      value: m.id,
      label: m.title ? `${m.name} · ${m.title}` : m.name,
    })),
  ];

  const handleConfirm = async () => {
    // Email is operator-entered (not pre-filled) — require it before we
    // create an account / send the welcome email.
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      addToast("error", "Enter a work email before onboarding.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await executeOnboarding({
        first_name: emp.first_name,
        last_name: emp.last_name,
        title: emp.title,
        work_email: trimmedEmail,
        department_id: emp.department_id,
        manager_id: managerId || null,
        start_date: startDate,
        employment_type: emp.employment_type,
      });
      setDone(res.summary);
      addToast("success", "Employee onboarded");
    } catch (e) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Onboarding failed. Please try again.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (cancelled) return null;

  if (done) {
    return (
      <Card>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
          <div>
            <p className="font-semibold text-text-primary">
              {fullName} onboarded
            </p>
            <p className="mt-0.5 text-sm text-text-muted">{done}</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-1 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-success" />
        <p className="text-[11px] uppercase tracking-wider text-text-muted">
          {section.title || "Ready to onboard"}
        </p>
      </div>
      <h3 className="text-lg font-semibold font-heading text-text-primary">
        {fullName}
      </h3>
      <p className="text-sm text-text-muted">
        {emp.title}
        {emp.department ? ` · ${emp.department}` : ""}
      </p>

      <div className="mt-4 space-y-3">
        <Input
          label="Email"
          type="email"
          value={email}
          placeholder={emp.email_placeholder || "name@company.com"}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Select
          label="Manager"
          value={managerId}
          onChange={(e) => setManagerId(e.target.value)}
          options={managerOptions}
        />
        <Input
          label="Start date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </div>

      <div className="mt-4 rounded-lg border border-dashed border-border bg-background p-3">
        <p className="mb-2 text-xs font-medium text-text-primary">
          This will:
        </p>
        <ul className="space-y-1">
          {section.steps_preview.map((s, i) => (
            <li
              key={i}
              className="flex items-center gap-2 text-xs text-text-muted"
            >
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" />
              {s}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button onClick={handleConfirm} isLoading={submitting}>
          <Rocket className="h-4 w-4" />
          Confirm &amp; onboard
        </Button>
        <Button
          variant="ghost"
          onClick={() => setCancelled(true)}
          disabled={submitting}
        >
          <X className="h-4 w-4" />
          Cancel
        </Button>
      </div>
    </Card>
  );
}

function CardTitle({ title }: { title?: string }) {
  if (!title) return null;
  return (
    <div className="mb-4 flex items-center gap-2">
      <div className="h-1 w-1 rounded-full bg-accent" />
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-muted">
        {title}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Stats — row of headline numbers with subtle accent treatment
// ════════════════════════════════════════════════════════════════════

function StatsCard({
  title,
  stats,
}: {
  title?: string;
  stats: { label: string; value: string | number | null }[];
}) {
  // Each stat renders as its own soft card so the row reads as a
  // bento-style overview rather than a single block. Matches the
  // Sana-style reference layout where each KPI is visually
  // separated.
  return (
    <div>
      {title && (
        <div className="mb-3 flex items-center gap-2 px-1">
          <div className="h-1 w-1 rounded-full bg-accent" />
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-muted">
            {title}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((s, i) => (
          <div
            key={i}
            className="rounded-2xl border border-border/40 bg-surface-muted/40 px-5 py-4"
          >
            <div className="text-3xl font-bold tabular-nums tracking-tight text-text-primary">
              {s.value == null ? "—" : s.value}
            </div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Bar chart — vertical, color-coded per category, with top legend +
//             axis labels + hover tooltip showing count + share %
// ════════════════════════════════════════════════════════════════════

function BarCard({
  title,
  data,
}: {
  title?: string;
  data: { label: string; value: number }[];
}) {
  if (!data.length) return null;

  const max = Math.max(1, ...data.map((d) => d.value));
  const total = data.reduce((a, b) => a + b.value, 0);

  // SVG geometry — H scales with category count so 5 bars don't
  // look stretched and 12 bars don't look cramped.
  const PAD_LEFT = 36;
  const PAD_RIGHT = 16;
  const PAD_TOP = 12;
  const PAD_BOTTOM = 64;
  const W = 720;
  const H = Math.max(280, 60 + data.length * 22);
  const plotW = W - PAD_LEFT - PAD_RIGHT;
  const plotH = H - PAD_TOP - PAD_BOTTOM;

  // Pretty Y-axis ticks — round up max to nearest "nice" number so
  // the gridlines don't sit at awkward fractions.
  const niceMax = niceCeil(max);
  const tickCount = 5;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) =>
    Math.round((niceMax * i) / tickCount),
  );

  const barSlot = plotW / data.length;
  // Bars take 60% of their slot — leaves breathing room and lines
  // up with the rotated x-label below.
  const barW = Math.max(8, Math.min(64, barSlot * 0.6));

  return (
    <Card>
      {title && (
        <div className="mb-3 flex items-center gap-2">
          <div className="h-1 w-1 rounded-full bg-accent" />
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-muted">
            {title}
          </div>
        </div>
      )}

      {/* Top legend — colored dot + label + count, wraps to lines */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px]">
        {data.map((d, i) => {
          const color = PALETTE[i % PALETTE.length];
          return (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: color }}
              />
              <span className="font-medium text-text-primary">{d.label}</span>
              <span className="tabular-nums text-text-muted">
                {fmt(d.value)}
              </span>
            </span>
          );
        })}
      </div>

      {/* Chart */}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        className="overflow-visible"
        role="img"
        aria-label={title || "Bar chart"}
      >
        {/* Y-axis gridlines + labels */}
        {ticks.map((t, i) => {
          const y = PAD_TOP + plotH - (t / niceMax) * plotH;
          return (
            <g key={i}>
              <line
                x1={PAD_LEFT}
                x2={W - PAD_RIGHT}
                y1={y}
                y2={y}
                stroke="rgb(var(--color-border))"
                strokeOpacity={i === 0 ? 0.8 : 0.4}
              />
              <text
                x={PAD_LEFT - 8}
                y={y + 4}
                textAnchor="end"
                className="fill-text-muted"
                style={{ fontSize: 11 }}
              >
                {t}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const color = PALETTE[i % PALETTE.length];
          const cx = PAD_LEFT + (i + 0.5) * barSlot;
          const x = cx - barW / 2;
          const h = (d.value / niceMax) * plotH;
          const y = PAD_TOP + plotH - h;
          const sharePct = total > 0 ? (d.value / total) * 100 : 0;
          return (
            <g key={i} className="bar-group">
              <title>
                {`${d.label}: ${fmt(d.value)} (${sharePct.toFixed(1)}%)`}
              </title>
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(2, h)}
                rx={Math.min(barW / 2, 6)}
                ry={Math.min(barW / 2, 6)}
                fill={color}
                style={{
                  transition: "opacity 200ms",
                }}
              />
              {/* Rotated x-axis label */}
              <text
                x={cx}
                y={PAD_TOP + plotH + 14}
                textAnchor="end"
                className="fill-text-muted"
                style={{ fontSize: 11 }}
                transform={`rotate(-35 ${cx} ${PAD_TOP + plotH + 14})`}
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </Card>
  );
}

/** Round a max value up to a "nice" gridline ceiling so axis ticks
 *  land on round numbers. 33 → 35, 47 → 50, 142 → 150. */
function niceCeil(v: number): number {
  if (v <= 5) return 5;
  if (v <= 10) return 10;
  if (v <= 25) return 25;
  if (v <= 50) return 50;
  if (v <= 100) return 100;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  let factor;
  if (norm <= 1.5) factor = 1.5;
  else if (norm <= 2) factor = 2;
  else if (norm <= 2.5) factor = 2.5;
  else if (norm <= 5) factor = 5;
  else factor = 10;
  return factor * mag;
}

// ════════════════════════════════════════════════════════════════════
// Pie / donut chart
// ════════════════════════════════════════════════════════════════════

function PieCard({
  title,
  data,
}: {
  title?: string;
  data: { label: string; value: number }[];
}) {
  const total = data.reduce((a, b) => a + b.value, 0);
  if (total <= 0) return null;
  const radius = 78;
  const strokeWidth = 22;
  const circumference = 2 * Math.PI * radius;
  let offsetSoFar = 0;

  return (
    <Card>
      <CardTitle title={title} />
      <div className="flex flex-col items-center gap-7 sm:flex-row sm:items-center">
        <svg
          width={radius * 2 + strokeWidth + 8}
          height={radius * 2 + strokeWidth + 8}
          aria-hidden="true"
          className="shrink-0"
        >
          <defs>
            {PALETTE.map((color, i) => (
              <linearGradient
                key={i}
                id={`pie-grad-${i}`}
                x1="0"
                y1="0"
                x2="1"
                y2="1"
              >
                <stop offset="0%" stopColor={color} stopOpacity="1" />
                <stop offset="100%" stopColor={color} stopOpacity="0.7" />
              </linearGradient>
            ))}
          </defs>
          <g
            transform={`translate(${radius + strokeWidth / 2 + 4}, ${radius + strokeWidth / 2 + 4})`}
          >
            {/* Subtle background ring so empty space reads cleaner */}
            <circle
              r={radius}
              cx={0}
              cy={0}
              fill="none"
              stroke="rgb(var(--color-border))"
              strokeOpacity={0.3}
              strokeWidth={strokeWidth}
            />
            {data.map((d, i) => {
              const frac = d.value / total;
              const len = circumference * frac;
              const dasharray = `${len} ${circumference - len}`;
              const dashoffset = -offsetSoFar;
              offsetSoFar += len;
              return (
                <circle
                  key={i}
                  r={radius}
                  cx={0}
                  cy={0}
                  fill="none"
                  stroke={`url(#pie-grad-${i % PALETTE.length})`}
                  strokeWidth={strokeWidth}
                  strokeDasharray={dasharray}
                  strokeDashoffset={dashoffset}
                  strokeLinecap="round"
                  transform="rotate(-90)"
                />
              );
            })}
            <text
              y="-2"
              textAnchor="middle"
              className="fill-text-primary"
              style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.5 }}
            >
              {fmt(total)}
            </text>
            <text
              y="16"
              textAnchor="middle"
              className="fill-text-muted"
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
              }}
            >
              Total
            </text>
          </g>
        </svg>
        <ul className="flex-1 space-y-2.5 text-sm w-full sm:w-auto">
          {data.map((d, i) => {
            const pct = (d.value / total) * 100;
            const color = PALETTE[i % PALETTE.length];
            return (
              <li
                key={i}
                className="flex items-center justify-between gap-4 rounded-lg px-2 py-1.5 hover:bg-surface-muted/40 transition-colors"
              >
                <span className="flex min-w-0 items-center gap-2.5 truncate">
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{
                      background: color,
                      boxShadow: `0 0 8px ${color}60`,
                    }}
                  />
                  <span className="truncate font-medium text-text-primary">
                    {d.label}
                  </span>
                </span>
                <span className="shrink-0 text-right tabular-nums">
                  <span className="font-semibold text-text-primary">
                    {fmt(d.value)}
                  </span>
                  <span className="ml-1.5 text-xs text-text-muted">
                    {pct.toFixed(0)}%
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// Line chart — smoothed sparkline with axis labels
// ════════════════════════════════════════════════════════════════════

function LineCard({
  title,
  data,
}: {
  title?: string;
  data: { label: string; value: number }[];
}) {
  if (data.length < 2) return null;
  const W = 600;
  const H = 200;
  const PAD = 36;
  const max = Math.max(...data.map((d) => d.value), 1);
  const min = Math.min(...data.map((d) => d.value), 0);
  const range = Math.max(1, max - min);
  const points = data.map((d, i) => {
    const x = PAD + (i / (data.length - 1)) * (W - PAD * 2);
    const y = H - PAD - ((d.value - min) / range) * (H - PAD * 2);
    return [x, y] as const;
  });

  // Smooth Bezier curve through every point — gives a much
  // softer Sana-like look vs straight polylines. Tension 0.5 is
  // the sweet spot between "too jagged" and "too sloppy".
  const bezierPath = (() => {
    if (points.length === 0) return "";
    let d = `M${points[0][0]},${points[0][1]}`;
    for (let i = 0; i < points.length - 1; i++) {
      const [x0, y0] = points[i];
      const [x1, y1] = points[i + 1];
      const cp1x = x0 + (x1 - x0) * 0.5;
      const cp2x = x0 + (x1 - x0) * 0.5;
      d += ` C${cp1x},${y0} ${cp2x},${y1} ${x1},${y1}`;
    }
    return d;
  })();
  const areaPath = `${bezierPath} L${W - PAD},${H - PAD} L${PAD},${H - PAD} Z`;

  // Last data point's value gets a callout chip — that's where the
  // eye lands first and what the user usually cares about.
  const last = points[points.length - 1];
  const lastValue = data[data.length - 1].value;
  const first = data[0].value;
  const delta = lastValue - first;
  const deltaPct = first > 0 ? ((delta / first) * 100).toFixed(0) : "0";
  const trendUp = delta >= 0;

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-1 w-1 rounded-full bg-accent" />
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-muted">
            {title}
          </div>
        </div>
        <div
          className={
            "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums " +
            (trendUp
              ? "bg-emerald-500/10 text-emerald-600"
              : "bg-rose-500/10 text-rose-600")
          }
        >
          <span>{trendUp ? "↑" : "↓"}</span>
          <span>{Math.abs(Number(deltaPct))}%</span>
        </div>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="line-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1B18FF" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#1B18FF" stopOpacity="0" />
          </linearGradient>
          <filter id="line-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {/* Y-axis grid */}
        {[0, 0.5, 1].map((p, i) => (
          <line
            key={i}
            x1={PAD}
            x2={W - PAD}
            y1={H - PAD - p * (H - PAD * 2)}
            y2={H - PAD - p * (H - PAD * 2)}
            stroke="rgb(var(--color-border))"
            strokeDasharray="3 3"
            strokeOpacity={0.6}
          />
        ))}
        <path d={areaPath} fill="url(#line-grad)" />
        <path
          d={bezierPath}
          fill="none"
          stroke="#1B18FF"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#line-glow)"
        />
        {points.map(([x, y], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={i === points.length - 1 ? 5 : 3}
            fill={i === points.length - 1 ? "#fff" : "#1B18FF"}
            stroke="#1B18FF"
            strokeWidth={i === points.length - 1 ? 2.5 : 0}
          />
        ))}
        {/* Final-point callout */}
        <g
          transform={`translate(${last[0] - 36}, ${Math.max(20, last[1] - 28)})`}
        >
          <rect
            width="72"
            height="22"
            rx="11"
            fill="#1B18FF"
          />
          <text
            x="36"
            y="15"
            textAnchor="middle"
            fill="white"
            style={{ fontSize: 11, fontWeight: 700 }}
          >
            {fmt(lastValue)}
          </text>
        </g>
        {/* X-axis labels (first / mid / last) */}
        {[0, Math.floor(data.length / 2), data.length - 1].map((idx, i) => (
          <text
            key={i}
            x={points[idx][0]}
            y={H - 8}
            textAnchor="middle"
            className="fill-text-muted"
            style={{ fontSize: 11 }}
          >
            {data[idx].label}
          </text>
        ))}
      </svg>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// Table
// ════════════════════════════════════════════════════════════════════

function TableCard({
  title,
  columns,
  rows,
}: {
  title?: string;
  columns: string[];
  rows: string[][];
}) {
  return (
    <Card padding={false}>
      {title && (
        <div className="px-4 pt-4">
          <CardTitle title={title} />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="border-b border-border bg-surface-muted/50 text-text-muted">
            <tr>
              {columns.map((c, i) => (
                <th
                  key={i}
                  className="px-4 py-2 text-left font-semibold uppercase tracking-wide text-[11px]"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr
                key={ri}
                className={
                  ri % 2 === 0
                    ? "border-b border-border/50"
                    : "border-b border-border/50 bg-surface-muted/30"
                }
              >
                {row.map((cell, ci) => (
                  <td key={ci} className="px-4 py-2 align-top">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// Text — narrative paragraph (with inline [N] citation footnotes)
// ════════════════════════════════════════════════════════════════════

function TextCard({
  title,
  body,
  citations,
}: {
  title?: string;
  body: string;
  citations: Map<number, Citation>;
}) {
  return (
    <Card>
      <CardTitle title={title} />
      <p className="text-sm leading-relaxed text-text-primary whitespace-pre-line">
        {tokenizeWithCitations(body, citations)}
      </p>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// Citation footnotes — tokenizer + footer
// ════════════════════════════════════════════════════════════════════

export function buildCitationMap(citations?: Citation[]): Map<number, Citation> {
  const m = new Map<number, Citation>();
  for (const c of citations ?? []) m.set(c.ref, c);
  return m;
}

/** Match `[1]`, `[12]`, `[1, 2]`, `[1,2,3]` — the planner is told
 *  to use single refs but groups happen, so we tolerate them. */
const CITATION_RE = /\[(\d+(?:\s*,\s*\d+)*)\]/g;

/**
 * Walks ``body`` and returns an array of React nodes — plain text
 * runs alternated with superscript footnote links for each ``[N]``
 * marker. If the marker doesn't resolve to a known citation, we
 * leave the original ``[N]`` text in place rather than swallowing
 * it (better to show "we cited something we forgot to register"
 * than to silently strip the marker).
 */
export function tokenizeWithCitations(
  body: string,
  citations: Map<number, Citation>,
): ReactNode[] {
  if (!body) return [];
  if (citations.size === 0) return [body];

  const out: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  CITATION_RE.lastIndex = 0;
  for (
    let match = CITATION_RE.exec(body);
    match !== null;
    match = CITATION_RE.exec(body)
  ) {
    const start = match.index;
    if (start > cursor) out.push(body.slice(cursor, start));
    const refs = match[1]
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n));
    const known = refs.filter((n) => citations.has(n));
    if (known.length === 0) {
      // Unknown ref — render the literal token so reviewers spot
      // the missing citation rather than silently losing it.
      out.push(match[0]);
    } else {
      out.push(
        <CitationRef
          key={`c-${key++}`}
          refs={known.map((n) => citations.get(n)!)}
        />,
      );
    }
    cursor = start + match[0].length;
  }
  if (cursor < body.length) out.push(body.slice(cursor));
  return out;
}

/** Inline superscript footnote — a tiny pill linking to the cited
 *  source. Multiple grouped refs render as comma-separated digits
 *  inside one pill: [1,2] → ¹·². */
function CitationRef({ refs }: { refs: Citation[] }) {
  // For grouped refs we link to the FIRST one — there's no clean
  // way to represent a multi-target link in HTML and grouped
  // citations are uncommon enough that this is fine.
  const primary = refs[0];
  const label = refs.map((r) => r.ref).join(",");
  const tooltip = refs
    .map((r) => `[${r.ref}] ${r.label}`)
    .join("\n");
  const className =
    "ml-0.5 inline-flex items-center justify-center rounded-full " +
    "bg-accent/10 px-1.5 align-super text-[10px] font-bold " +
    "text-accent leading-[1.4] hover:bg-accent/20 hover:text-accent-700 " +
    "transition-colors no-underline";

  if (!primary.url) {
    return (
      <span className={className} title={tooltip}>
        {label}
      </span>
    );
  }
  // Internal route → react-router Link; external URL → plain anchor
  // with rel=noreferrer so we don't leak referrer to third parties.
  if (primary.url.startsWith("/")) {
    return (
      <Link to={primary.url} className={className} title={tooltip}>
        {label}
      </Link>
    );
  }
  return (
    <a
      href={primary.url}
      target="_blank"
      rel="noreferrer"
      className={className}
      title={tooltip}
    >
      {label}
    </a>
  );
}

const SOURCE_ICONS: Record<Citation["source_type"], LucideIcon> = {
  document: FileText,
  announcement: Megaphone,
  job_posting: Briefcase,
  template: Layers,
  employee: UserIcon,
  report: TrendingUp,
  policy: BookOpen,
  leave: Calendar,
  invoice: Receipt,
  complaint: FileText,
  external: ExternalLink,
  other: FileText,
};

function CitationsFooter({ citations }: { citations: Citation[] }) {
  return (
    <Card>
      <CardTitle title="Sources" />
      <ol className="space-y-2 text-sm">
        {citations.map((c) => {
          const Icon = SOURCE_ICONS[c.source_type] ?? FileText;
          const inner = (
            <>
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/10 text-[10px] font-bold text-accent">
                {c.ref}
              </span>
              <Icon className="h-4 w-4 shrink-0 text-text-muted" />
              <span className="flex-1 truncate text-text-primary">
                {c.label}
              </span>
              {c.url && (
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-text-muted" />
              )}
            </>
          );
          if (!c.url) {
            return (
              <li
                key={c.ref}
                className="flex items-center gap-2.5 rounded-md border border-border/40 bg-surface px-3 py-2"
              >
                {inner}
              </li>
            );
          }
          if (c.url.startsWith("/")) {
            return (
              <li key={c.ref}>
                <Link
                  to={c.url}
                  className="flex items-center gap-2.5 rounded-md border border-border/40 bg-surface px-3 py-2 hover:border-accent/40 hover:bg-accent/5 transition-colors"
                >
                  {inner}
                </Link>
              </li>
            );
          }
          return (
            <li key={c.ref}>
              <a
                href={c.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2.5 rounded-md border border-border/40 bg-surface px-3 py-2 hover:border-accent/40 hover:bg-accent/5 transition-colors"
              >
                {inner}
              </a>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════
// Action row (Download PDF / Email / Schedule weekly)
// ════════════════════════════════════════════════════════════════════

const ACTION_ICONS: { match: RegExp; icon: LucideIcon }[] = [
  { match: /pdf|download/i, icon: Download },
  { match: /email|mail|send/i, icon: Mail },
  { match: /schedule|weekly|monthly|cron/i, icon: Calendar },
  { match: /.*/, icon: Sparkles },
];

function ActionRow({
  actions,
  onAction,
}: {
  actions: string[];
  onAction?: (label: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-1">
      {actions.map((a, i) => {
        const Icon = ACTION_ICONS.find((x) => x.match.test(a))!.icon;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onAction?.(a)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3.5 py-1.5 text-xs font-semibold text-text-primary hover:border-accent/40 hover:bg-accent/5 hover:text-accent active:scale-[0.97] transition-all"
          >
            <Icon className="h-3.5 w-3.5" />
            {a}
          </button>
        );
      })}
    </div>
  );
}

function SuggestionRow({
  suggestions,
  onSuggestion,
}: {
  suggestions: string[];
  onSuggestion?: (text: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {suggestions.map((s, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSuggestion?.(s)}
          className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium text-text-primary hover:border-accent/50 hover:bg-accent/5 hover:text-accent active:scale-[0.97] transition-all"
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════

function fmt(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

export default ReportRenderer;
